import { Box, Stack, Typography } from '@mui/material'
import { FC, ReactNode, useMemo, useState } from 'react'
import {
  VatValidationStatus,
  useCreateInvoicingAddress,
  useGetInvoicingAddressesList,
  useUpdateInvoicingAddress
} from 'dataQueries/invoicingAddresses.query'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { Country } from 'constants/country'
import { InvoicingAddressItem } from './InvoicingAddressItem.component'
import { InvoicingAddressPopup } from 'components/common/InvoicingAddressPopup'
import { MUIButton } from 'components/common/MUIButton'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

/**
 * Public props for {@link InvoicingAddressesController}.
 */
export interface InvoicingAddressesControllerProps {
  /**
   * Title or header element for the section.
   * Can be a string, typography component, or any React node.
   */
  sectionTitle: ReactNode
}

/**
 * Controller component responsible for displaying, creating and editing
 * the user's invoicing addresses.
 *
 * @remarks
 * This component orchestrates:
 * - Fetching the invoicing addresses list.
 * - Controlling create & update modals.
 * - Mapping user input to API mutations.
 * - Showing feedback through snackbars.
 *
 * It renders:
 * - A list of {@link InvoicingAddressItem}
 * - A create popup
 * - An edit popup with prefilled data.
 *
 * @component
 * @example
 * ```tsx
 * <InvoicingAddressesController
 *   sectionTitle={<Typography variant="h6">Invoicing addresses</Typography>}
 * />
 * ```
 */
export const InvoicingAddressesController: FC<InvoicingAddressesControllerProps> = ({
  sectionTitle
}) => {

  /** Translation hook covering section and popup messages */
  const { t } = useTranslation(['invoicing_addresses_profile', 'invoicing_address_popup'])

  /** Snackbar service used for success/warning toasts */
  const { spawnSuccessToast, spawnWarningToast } = useSnackbar()

  /** Fetches the list of invoicing addresses */
  const addressList = useGetInvoicingAddressesList()

  /** Mutation for updating an existing address */
  const updateAddressMutation = useUpdateInvoicingAddress()

  /** Mutation for creating a new address */
  const createInvoicingAddressMutation = useCreateInvoicingAddress()

  /** Stores the ID of the currently edited address */
  const [updateId, setUpdateId] = useState<string | null>(null)

  /** Controls visibility of the create address modal */
  const [createOpen, setCreateOpen] = useState(false)

  /**
   * Memoized currently selected address for editing.
   *
   * @returns The address matching {@link updateId} or `undefined`
   */
  const updatedEntry = useMemo(() => {
    if (!updateId || !addressList.data) return undefined

    return addressList.data.find((address) => address.id === updateId)
  }, [updateId, addressList.data])

  return (
    <Box marginTop={4}>

      {/* Section header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>

        {sectionTitle}

        {/* Create new address button */}
        <MUIButton onClick={() => setCreateOpen(true)}>
          {t('create_new_btn')}
        </MUIButton>

      </Stack>

      {/* Address list container */}
      <BorderBoxWrapper
        width="100%"
        p={2}
        elevation="sm"
        marginTop={2}
      >
        <Stack gap={2}>

          {(addressList.data ?? []).map((address) => (
            <InvoicingAddressItem
              key={address.id}
              address={address}
              onEdit={(addressId) => setUpdateId(addressId)}
            />
          ))}

          {addressList.isSuccess && addressList.data.length === 0 &&
            <Typography variant="text-md">
              {t('no_address_yet')}
            </Typography>
          }

        </Stack>
      </BorderBoxWrapper>

      {/* CREATE ADDRESS POPUP */}
      <InvoicingAddressPopup
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        query={createInvoicingAddressMutation}
        onSubmit={(values) => createInvoicingAddressMutation.mutate(
          {
            addressName: values.addressName,
            companyOrIndividualName: values.companyOrIndividualName,
            streetAndNumber: values.streetAndNumber,
            apartmentNumber: values.apartmentNumber,
            city: values.city,
            region: values.region,
            country: values.country as Country,
            postalCode: values.postalCode,
            vatNumber: values.vatNumber?.trim() || undefined,
          },
          {
            onSuccess: (data) => {
              const status = data.validationStatus
              switch (status) {
                case VatValidationStatus.VALID:
                  spawnSuccessToast(t('invoicing_address_popup:create.success_toast_vat_valid'))
                  break
                case VatValidationStatus.INVALID:
                  spawnWarningToast(
                    t('invoicing_address_popup:create.success_toast_vat_invalid'),
                    { timeout: 7000 }
                  )
                  break
                default:
                  spawnSuccessToast(t('invoicing_address_popup:create.success_toast'))
              }
              setCreateOpen(false)
            }
          }
        )}
      />

      {/* UPDATE ADDRESS POPUP */}
      <InvoicingAddressPopup
        key={updateId ?? 'closed'}
        mode="edit"
        isOpen={updateId !== null}
        onClose={() => setUpdateId(null)}
        query={updateAddressMutation}
        onSubmit={(values) => !!updateId && updateAddressMutation.mutate(
          {
            addressId: updateId,
            addressName: values.addressName,
            companyOrIndividualName: values.companyOrIndividualName,
            streetAndNumber: values.streetAndNumber,
            apartmentNumber: values.apartmentNumber,
            city: values.city,
            region: values.region,
            country: values.country as Country,
            postalCode: values.postalCode,
            vatNumber: values.vatNumber?.trim() || undefined,
          },
          {
            onSuccess: (response) => {
              const status = response.validationStatus
              switch (status) {
                case VatValidationStatus.VALID:
                  spawnSuccessToast(t('invoicing_address_popup:edit.success_toast_vat_valid', {
                    addressName: values.addressName
                  }))
                  break
                case VatValidationStatus.INVALID:
                  spawnWarningToast(
                    t('invoicing_address_popup:edit.success_toast_vat_invalid', {
                      addressName: values.addressName
                    }),
                    { timeout: 7000 }
                  )
                  break
                default:
                  spawnSuccessToast(
                    t('invoicing_address_popup:edit.success_toast', {
                      addressName: values.addressName
                    })
                  )
              }
              setUpdateId(null)
            }
          }
        )}
        defaultValues={
          !!updatedEntry
            ? {
              addressName: updatedEntry?.addressName ?? '',
              companyOrIndividualName: updatedEntry?.companyOrIndividualName ?? '',
              streetAndNumber: updatedEntry?.streetAndNumber ?? '',
              apartmentNumber: updatedEntry?.apartmentNumber ?? '',
              city: updatedEntry?.city ?? '',
              region: updatedEntry?.region ?? '',
              country: updatedEntry?.country ?? '',
              postalCode: updatedEntry?.postalCode ?? '',
              vatNumber: updatedEntry?.vatNumber ?? '',
            }
            : undefined
        }
      />

    </Box>
  )
}
